import { FC, ChangeEvent, DragEvent, useState } from 'react'
import { Box, FormControlLabel, Checkbox } from '@mui/material'
import { useThemeColors } from 'hooks/useThemeColors'

/* Utils */
import { DevicesColumn } from 'models'

/* Components */
import { Popper } from './columnPicker.styles'
import { ThemeButton } from 'ui/components/buttons'
interface Props {
  anchorEl: HTMLElement | null
  open: boolean
  columns: DevicesColumn[]
  handleChangeColumns: (event: ChangeEvent<HTMLInputElement>) => void
  handleRestoreDefaultColumns: () => void
  isUpdating?: boolean
  className?: string
  /* Given, rows carry a drag handle and dropping one moves the column to the target's place. */
  onReorder?: (columnName: string, targetName: string) => void
  restoreDisabled?: boolean
}

const ColumnPicker: FC<Props> = ({
  anchorEl,
  open,
  columns,
  handleChangeColumns,
  handleRestoreDefaultColumns,
  isUpdating = false,
  className,
  onReorder,
  restoreDisabled = false
}) => {
  const [dragged, setDragged] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)

  const dragProps = (name: string) =>
    onReorder
      ? {
          draggable: !isUpdating,
          onDragStart: (event: DragEvent) => {
            event.dataTransfer.effectAllowed = 'move'
            setDragged(name)
          },
          onDragOver: (event: DragEvent) => {
            if (!dragged) return
            event.preventDefault()
            setDropTarget(name)
          },
          onDrop: (event: DragEvent) => {
            event.preventDefault()
            if (dragged && dragged !== name) onReorder(dragged, name)
            setDragged(null)
            setDropTarget(null)
          },
          onDragEnd: () => {
            setDragged(null)
            setDropTarget(null)
          }
        }
      : {}

  return (
    <Popper id="placement-popper" open={open} anchorEl={anchorEl} placement="left-start" className={className}>
      <Box className="content">
        <Box className="list">
          {columns.map((column) => (
            <Box
              className={`list-item${dragged === column.name ? ' is-dragging' : ''}${
                dropTarget === column.name && dragged !== column.name ? ' is-drop-target' : ''
              }`}
              key={column.name}
              {...dragProps(column.name)}
            >
              <FormControlLabel
                className={`${column.active ? 'is-checked' : ''}`}
                control={
                  <Checkbox
                    checked={column.active}
                    onChange={handleChangeColumns}
                    icon={<ColumnUncheckedIcon />}
                    checkedIcon={<ColumnCheckedIcon />}
                    disabled={isUpdating}
                  />
                }
                label={column.displayName}
                name={column.name}
                disabled={isUpdating}
              />
              {onReorder && (
                <Box component="span" className="drag-handle" aria-hidden>
                  <DragHandleIcon />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <Box className="button-wrapper">
        <ThemeButton
          themevariant="hollow"
          onClick={handleRestoreDefaultColumns}
          disabled={isUpdating || restoreDisabled}
        >
          Restore default
        </ThemeButton>
      </Box>
    </Popper>
  )
}

export default ColumnPicker

const ColumnCheckedIcon = () => {
  const getColor = useThemeColors()

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.00146 8.50159L7.32896 10.8291C7.4182 10.9183 7.56678 10.9036 7.63679 10.7986L11.2515 5.37659M5.60156 2.10156H10.4016C12.3346 2.10156 13.9016 3.66857 13.9016 5.60156V10.4016C13.9016 12.3346 12.3346 13.9016 10.4016 13.9016H5.60156C3.66857 13.9016 2.10156 12.3346 2.10156 10.4016V5.60156C2.10156 3.66857 3.66857 2.10156 5.60156 2.10156Z"
        stroke={getColor('checkbox-checked')}
        strokeLinecap="round"
      />
    </svg>
  )
}

const ColumnUncheckedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.09961" y="2.10156" width="11.8" height="11.8" rx="3.5" stroke="#998D96" />
  </svg>
)

const DragHandleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="3.5" r="1.1" />
    <circle cx="10" cy="3.5" r="1.1" />
    <circle cx="6" cy="8" r="1.1" />
    <circle cx="10" cy="8" r="1.1" />
    <circle cx="6" cy="12.5" r="1.1" />
    <circle cx="10" cy="12.5" r="1.1" />
  </svg>
)
